import React from 'react';
import { Instagram, ExternalLink } from 'lucide-react';

const Gallery = () => {
  const instagramUsername = "ifixappletech";
  const instagramUrl = `https://www.instagram.com/${instagramUsername}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-sky-50/30">
      {/* Hero Section */}
      <section className="relative py-6 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Our <span className="text-sky-500">Repair Gallery</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto mb-8">
            Check out our latest repair work and success stories on Instagram!
          </p>
        </div>
      </section>

      {/* Instagram Feed Section */}
      <section className="py-4 md:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Instagram Gallery Card */}
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl border border-slate-200 text-center">
            
            {/* Instagram Icon with Wrench */}
            <div className="flex justify-center items-center gap-4 mb-6">
              <div className="w-16 h-16 md:w-24 md:h-24 bg-gradient-to-br from-sky-400 to-sky-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Instagram className="w-8 h-8 md:w-12 md:h-12 text-white" />
              </div>
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/83cd0194-c314-4ffc-99ab-72d210cfce32/images/bafafde66f4fbee106085126aaeb4405370a39525de075161fc2f20e1a89de54.jpeg"
                alt="Repair Icon"
                className="h-16 w-16 md:h-24 md:w-24 object-contain opacity-70"
              />
            </div>

            {/* Content */}
            <h2 className="text-xl md:text-3xl font-bold text-slate-900 mb-4">
              View Our Latest Repairs on Instagram
            </h2>
            <p className="text-lg text-slate-600 mb-4 max-w-2xl mx-auto leading-relaxed">
              We post photos of our repair work daily! Follow <span className="font-semibold text-sky-600">@{instagramUsername}</span> to see:
            </p>

            {/* Features Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 max-w-3xl mx-auto">
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="font-semibold text-slate-900">📱 Screen Repairs</p>
                <p className="text-sm text-slate-600">Before & after photos</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="font-semibold text-slate-900">🔧 Custom Builds</p>
                <p className="text-sm text-slate-600">Gaming PCs & upgrades</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="font-semibold text-slate-900">💻 MacBook Repairs</p>
                <p className="text-sm text-slate-600">Professional service</p>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <a
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-br from-sky-500 to-sky-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-xl transition-all hover:scale-105"
              >
                <Instagram className="w-6 h-6" />
                View Gallery on Instagram
                <ExternalLink className="w-5 h-5" />
              </a>
              
              <a
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-white border-2 border-slate-300 text-slate-900 px-8 py-4 rounded-full font-semibold text-lg hover:bg-slate-50 transition-all"
              >
                Follow @{instagramUsername}
              </a>
            </div>

            {/* Note */}
            <p className="text-sm text-slate-500">
              Updated daily with new repair projects and customer success stories
            </p>
          </div>

          {/* Why Instagram Section */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="text-3xl mb-3">📸</div>
              <h3 className="font-bold text-slate-900 mb-2">Real-Time Updates</h3>
              <p className="text-sm text-slate-600">See our latest projects as soon as we finish them</p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="text-3xl mb-3">💬</div>
              <h3 className="font-bold text-slate-900 mb-2">Customer Reviews</h3>
              <p className="text-sm text-slate-600">Read what our customers say about their repairs</p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="text-3xl mb-3">🎥</div>
              <h3 className="font-bold text-slate-900 mb-2">Behind the Scenes</h3>
              <p className="text-sm text-slate-600">Watch videos of complex repairs and upgrades</p>
            </div>
          </div>

          {/* Service Info Banner */}
          <div className="mt-12">
            <div className="bg-white/60 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm p-6 md:p-8">
              <div className="text-center space-y-3">
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
                  Same Day Repairs on All Apple Devices
                </h2>
                <p className="text-base md:text-lg text-slate-600">
                  Please call or text for a price quote/estimate
                </p>
                <p className="text-sm md:text-base text-sky-600 font-semibold">
                  We guarantee to beat any local competitor's pricing — quality repairs at the best rates
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center mt-4">
                  <a
                    href="tel:+19782576427"
                    className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-sky-500 text-white font-semibold text-sm hover:bg-sky-600 transition-all"
                  >
                    Call: (978) 257-6427
                  </a>
                  <a
                    href="sms:+19782576427"
                    className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-white border-2 border-sky-500 text-sky-600 font-semibold text-sm hover:bg-sky-50 transition-all"
                  >
                    Text Us
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="mt-12">
            <div className="bg-gradient-to-br from-sky-500 to-sky-600 rounded-2xl p-8 md:p-12 shadow-xl text-center">
              <h3 className="text-3xl font-bold text-white mb-4">
                Ready to Fix Your Device?
              </h3>
              <p className="text-sky-50 text-lg mb-6 max-w-2xl mx-auto">
                Impressed by our work? Contact us today for same-day repairs with lifetime warranty!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:+19782576427"
                  className="inline-flex items-center justify-center rounded-full bg-white text-sky-600 px-8 py-4 text-lg font-semibold hover:bg-sky-50 transition-all shadow-lg"
                >
                  Call: (978) 257-6427
                </a>
                <a
                  href="sms:+19782576427"
                  className="inline-flex items-center justify-center rounded-full bg-transparent border-2 border-white text-white px-8 py-4 text-lg font-semibold hover:bg-white/10 transition-all"
                >
                  Text Us
                </a>
              </div>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
};

export default Gallery;
